/* eslint-disable no-case-declarations */
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import {
  DetailListActions,
  PrimaryActionsConfig,
  GroupedDropdownActionConfig,
} from "Components/General/DetailListActions";
import { EmailSendModal } from "Pages/Project/Communication/EmailSendModal/EmailSendModal";
import { emailSendModal } from "Pages/Project/Communication/utils/emailModalProps";
import { observer } from "mobx-react";
import { viewsStore } from "../Stores/ViewsStore";
import { printJobSettingsStore } from "Pages/Project/Settings/Stores/PrintJobStore";
import { projectsDataStore } from "Stores/ProjectsStore";
import { ViewsExportModal } from "./ViewsExportModal";
import { StartWorkflowWithFiles } from "Pages/Project/Documents/CollectionFolder/StartWorkflowWithFiles";
import { modalProps } from "Pages/Project/QA/utils/modalProps";
import { WithModal, withModal } from "Components/General/Modal/withModal";
import { collectionFolderStore } from "Pages/Project/Documents/CollectionFolder/CollectionFolderStore";
import { CollectionFolderFile } from "Pages/Project/Documents/CollectionFolder/interfaces";
import { PrintForm } from "Components/FilePrint/Components/PrintForm";
import { useEmailAttachments } from "utils/hooks/useEmailAttachments";
import { emailSubmitStore } from "Pages/Project/Communication/Stores/EmailSubmitStore";
import { useCallback, useMemo } from "react";
import { fileDownloadStore } from "Pages/Project/Documents/FileDownload/FileDownloadStore";

interface ViewsActionsMenuProps extends WithModal {
  fileIds: number[];
  viewExportInfo: API_ViewRequest;
  fetch: () => void;
  files: API_ViewInformation[];
  resetSelection: () => void;
}

type ActionType = "download" | "send" | "print" | "export" | "start_workflow" | "to_collection" | "from_collection";

export const ViewsActionsMenu = withModal(
  observer(({ fileIds, viewExportInfo, files, fetch: fetchData, showModal, resetSelection }: ViewsActionsMenuProps) => {
    const { t } = useTranslation(I18N_NS.PROJECT);
    const { checkAttachmentsAccess } = useEmailAttachments(emailSubmitStore);

    const handleMenuClick = useCallback(
      async ({ key }: { key: ActionType }) => {
        if (!fileIds.length) {
          message.error(t("No files were selected"));
          return;
        }

        const isAbletoDownload = files.some((file) => file.fileActions.download);
        const isAbletoDownloadWithWatermark = files.some((file) => file.fileActions.downloadWithWatermark);

        try {
          switch (key) {
            case "download":
              const filesToDownload = files.map((item) => {
                if (item.state === "SYMLINK") {
                  return { id: item.linkId, state: item.state };
                } else if (item.state === "EXTERN_SYMLINK") {
                  return { id: item.id, state: item.state, linkId: item.linkId };
                } else {
                  return { id: item.id, state: item.state };
                }
              });
              await fileDownloadStore.initModal(
                filesToDownload,
                !isAbletoDownload && isAbletoDownloadWithWatermark ? "RWM" : "ALL"
              );
              break;
            case "send":
              checkAttachmentsAccess(viewsStore.getFilesFromIds(fileIds));
              await new Promise((res) => {
                showModal(EmailSendModal, {
                  modalProps: { ...emailSendModal },
                  componentProps: {
                    mailType: "attach",
                  },
                });
                res(undefined);
              });
              break;
            case "print":
              await new Promise((res) => {
                showModal(PrintForm, {
                  modalProps: { style: { maxWidth: 1300, top: 20 }, width: "100%", footer: null, title: " " },
                  componentProps: {
                    files: files,
                  },
                });
                res(undefined);
              });
              break;
            case "start_workflow":
              await new Promise<void>((res) => {
                showModal(StartWorkflowWithFiles, {
                  modalProps: { ...modalProps, title: t("Start workflow") },
                  componentProps: { fileIds, startFrom: "VIEW" },
                });
                res();
              });
              break;
            case "export":
              await new Promise((res) => {
                showModal(ViewsExportModal, {
                  modalProps: {
                    footer: null,
                    maskClosable: false,
                    width: "100%",
                    style: { maxWidth: 1000, top: 20 },
                    title: t("Create a list"),
                  },
                  componentProps: {
                    fileIds,
                    viewExportInfo,
                  },
                });
                res(undefined);
              });
              break;
            case "to_collection":
              await new Promise((res) => {
                collectionFolderStore.addFiles(files as CollectionFolderFile[]);
                res(undefined);
              });
              break;
            case "from_collection":
              files.forEach((f) => collectionFolderStore.removeFile(f.id));
              break;
            default:
              message.warning(t("Invalid action"));
              break;
          }
        } catch (error) {
          console.error("Error in action:", error);
          message.error(t("An error occurred while processing your request"));
        }
      },
      [fileIds, files, viewExportInfo, showModal, checkAttachmentsAccess]
    );

    const primaryActions = useMemo<PrimaryActionsConfig[]>(
      () => [
        {
          label: t("Refresh"),
          onClick: () => {
            resetSelection();
            fetchData();
          },
          icon: "sync",
        },
        {
          label: t("Collection folder"),
          onClick: collectionFolderStore.toggleModal,
          badgeCount: collectionFolderStore.fileIds.length,
          icon: "folder-open",
        },
      ],
      [fetchData, resetSelection, collectionFolderStore.fileIds.length]
    );

    const dropdownActions = useMemo<GroupedDropdownActionConfig>(() => {
      const config: GroupedDropdownActionConfig = {};

      // Basics group
      config[t("Basics")] = [
        {
          label: `${t("Download")}...`,
          onClick: () => handleMenuClick({ key: "download" }),
          icon: ["far", "download"],
        },
        {
          label: t("Add to collection folder"),
          onClick: () => handleMenuClick({ key: "to_collection" }),
          icon: ["far", "folder-open"],
        },
      ];

      // Multiple files group
      config[t("Multiple files")] = [
        {
          label: `${t("Export as Excel file")}...`,
          onClick: () => handleMenuClick({ key: "export" }),
          icon: ["far", "file-excel"],
        },
      ];

      // Other modules group
      const otherModules = [];
      if (projectsDataStore.currentModules.includes("MAIL")) {
        otherModules.push({
          label: `${t("Send")}...`,
          onClick: () => handleMenuClick({ key: "send" }),
          icon: ["far", "envelope"],
        });
      }
      if (printJobSettingsStore.allowPrint) {
        otherModules.push({
          label: `${t("Print job")}...`,
          onClick: () => handleMenuClick({ key: "print" }),
          icon: ["far", "print"],
        });
      }
      if (projectsDataStore.currentModules.includes("WORKFLOW")) {
        otherModules.push({
          label: `${t("Start workflow")}...`,
          onClick: () => handleMenuClick({ key: "start_workflow" }),
          icon: ["far", "code-branch"],
        });
      }

      if (otherModules.length > 0) {
        config[t("Other modules")] = otherModules;
      }

      return config;
    }, [handleMenuClick, t]);

    return <DetailListActions dropdownFirst primaryActions={primaryActions} groupedDropdownActions={dropdownActions} />;
  })
);
